import { BookingContext, BookingStep, StepState } from '@/lib/types/operations/booking'
import type {
  ColouredPpfCoverageTier,
  MaterialTier,
  PpfCoverage,
  PpfFilmTier,
} from '@/lib/estimator/pricing/price-book'
import type { WrapCoverageTier } from '@/lib/estimator/types'
import { VehicleItem } from '@/lib/types/vehicle'
import { VehicleType } from '@prisma/client'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Per-service customer picks. Which fields apply depends on the service's
// packageConfig.kind: full_wrap uses materialTier + coverageTier; commercial_print
// uses coverageTier; commercial_decal uses decalPackage; ppf uses ppfCoverage +
// filmTier; coloured_ppf uses colouredPpfCoverage.
export interface ServiceTierSelection {
  materialTier?: MaterialTier
  coverageTier?: WrapCoverageTier
  decalPackage?: 'light' | 'bold'
  ppfCoverage?: PpfCoverage
  filmTier?: PpfFilmTier
  colouredPpfCoverage?: ColouredPpfCoverageTier
}

export interface VehicleDetails {
  vehicleType: VehicleType | null
  make: string | null
  model: string | null
  year: string | null
  trim?: string | null
  licensePlate?: string | null
  vin?: string | null
  vehicleDefinitionId?: string | null
}

interface BookingState {
  current: {
    activeStep: BookingStep | null
    stepStates: Record<BookingStep, StepState>
    visitedSteps?: BookingStep[]
    context: BookingContext
    isDrawerOpen: boolean
    isSubmitting: boolean
    showVehicleSelect: boolean
    userVehicles: VehicleItem[]
    isLoadingVehicles: boolean
    isLoadingServices: boolean
    vehicleModalShown: boolean
    justAuthenticated: boolean
  }
  persisted: {
    // Identity of whoever created/owns the in-flight booking. A userId when an
    // authenticated user created or claimed it; null when created anonymously
    // (or after a clear). Used to gate rehydration on return so a different
    // account never inherits another user's partial booking. See
    // src/app/book/lib/booking-owner-gate.ts.
    ownerId: string | null
    shopLocationId: string | null
    shopTimezone: string | null
    vehicleDetails: VehicleDetails | null
    selectedVehicleId: string | null
    selectedServiceIds: string[]
    // Customer-chosen material / coverage tier per service, keyed by service
    // id. Only ever populated for services whose packageConfig.kind is
    // full_wrap (materialTier) or commercial_wrap (coverageTier); read by
    // calculateReviewServices / calculateAppointmentTotal at review time.
    serviceTiers: Record<string, ServiceTierSelection>
    selectedOption: {
      optionId: string
      wallClockStart: string
      wallClockEnd: string
      nightsOvernight: number
      installerCount: number
      appointmentId?: string
      expiresAt?: string
    } | null
    contactInfo: {
      email: string | null
      phone: string | null
    } | null
  }
}

// The default shape of the persisted slice, exported so the store's rehydrate
// migration can backfill keys that a returning visitor's stored blob predates.
// redux-persist's autoMergeLevel1 restores `persisted` wholesale, so a key
// added here after their blob was written arrives as undefined without it.
export const bookingPersistedDefaults: BookingState['persisted'] = {
  ownerId: null,
  shopLocationId: null,
  shopTimezone: null,
  vehicleDetails: null,
  selectedVehicleId: null,
  selectedServiceIds: [],
  serviceTiers: {},
  selectedOption: null,
  contactInfo: null
}

const initialState: BookingState = {
  current: {
    activeStep: null,
    stepStates: {
      vehicle: { isValid: false, errors: [] },
      services: { isValid: false, errors: [] },
      schedule: { isValid: false, errors: [] },
      review: { isValid: false, errors: [] }
    },
    visitedSteps: [],
    context: {
      isAuthenticated: false,
      canProceed: false,
      validationErrors: [],
      activeStep: null
    },
    isDrawerOpen: false,
    isSubmitting: false,
    showVehicleSelect: false,
    userVehicles: [],
    isLoadingVehicles: false,
    isLoadingServices: false,
    vehicleModalShown: false,
    justAuthenticated: false
  },
  persisted: { ...bookingPersistedDefaults }
}

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    setActiveStep: (state, action: PayloadAction<BookingStep>) => {
      console.log('[bookingSlice] setActiveStep', {
        from: state.current.activeStep,
        to: action.payload,
        timestamp: new Date().toISOString()
      })

      state.current.activeStep = action.payload

      // Mark step as visited when navigating to it
      if (!state.current.visitedSteps) {
        state.current.visitedSteps = []
      }
      if (!state.current.visitedSteps.includes(action.payload)) {
        state.current.visitedSteps.push(action.payload)
      }
    },
    setStepState: (state, action: PayloadAction<{ step: BookingStep; state: StepState }>) => {
      console.log('[bookingSlice] setStepState', {
        step: action.payload.step,
        newState: action.payload.state,
        currentStepStates: state.current.stepStates,
        timestamp: new Date().toISOString()
      })
      state.current.stepStates[action.payload.step] = action.payload.state
    },
    setBookingContext: (state, action: PayloadAction<Partial<BookingContext>>) => {
      console.log('[bookingSlice] setBookingContext', {
        payload: action.payload,
        currentContext: state.current.context,
        timestamp: new Date().toISOString()
      })

      // Update the context
      state.current.context = { ...state.current.context, ...action.payload }

      // Sync validation errors with step states
      if (action.payload.validationErrors) {
        const canonicalStep = (field: string): BookingStep =>
          field as BookingStep

        // Group validation errors by canonical step
        const errorsByStep = action.payload.validationErrors.reduce((acc, error) => {
          const step = canonicalStep(error.field)
          if (!acc[step]) {
            acc[step] = []
          }
          acc[step].push({
            field: error.field,
            message: error.message
          })
          return acc
        }, {} as Record<BookingStep, Array<{ field: string; message: string }>>)

        // Update step states with their corresponding errors (only for canonical steps)
        Object.entries(errorsByStep).forEach(([step, errors]) => {
          if (state.current.stepStates[step as BookingStep]) {
            state.current.stepStates[step as BookingStep] = {
              isValid: false,
              errors
            }
          }
        })
      }
    },
    setDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.current.isDrawerOpen = action.payload
    },
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.current.isSubmitting = action.payload
    },
    // Vehicle selection actions
    setShowVehicleSelect: (state, action: PayloadAction<boolean>) => {
      state.current.showVehicleSelect = action.payload
      if (action.payload) {
        state.current.vehicleModalShown = true
      }
    },
    setUserVehicles: (state, action: PayloadAction<VehicleItem[]>) => {
      state.current.userVehicles = action.payload
    },
    setLoadingVehicles: (state, action: PayloadAction<boolean>) => {
      state.current.isLoadingVehicles = action.payload
    },
    setLoadingServices: (state, action: PayloadAction<boolean>) => {
      state.current.isLoadingServices = action.payload
    },
    setJustAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.current.justAuthenticated = action.payload
    },
    setSelectedVehicleId: (state, action: PayloadAction<string | null>) => {
      state.persisted.selectedVehicleId = action.payload
    },
    setBookingOwner: (state, action: PayloadAction<string | null>) => {
      state.persisted.ownerId = action.payload
    },
    // Persisted state actions
    setShopLocation: (state, action: PayloadAction<string>) => {
      state.persisted.shopLocationId = action.payload
    },
    setShopTimezone: (state, action: PayloadAction<string>) => {
      state.persisted.shopTimezone = action.payload
    },
    setVehicleDetails: (state, action: PayloadAction<VehicleDetails>) => {
      state.persisted.vehicleDetails = action.payload
    },
    setSelectedServices: (state, action: PayloadAction<string[]>) => {
      state.persisted.selectedServiceIds = action.payload
    },
    addSelectedService: (state, action: PayloadAction<string>) => {
      if (!state.persisted.selectedServiceIds.includes(action.payload)) {
        state.persisted.selectedServiceIds.push(action.payload)
      }
    },
    removeSelectedService: (state, action: PayloadAction<string>) => {
      state.persisted.selectedServiceIds = state.persisted.selectedServiceIds.filter(
        id => id !== action.payload
      )
      if (state.persisted.serviceTiers) {
        delete state.persisted.serviceTiers[action.payload]
      }
    },
    setServiceTier: (
      state,
      action: PayloadAction<{ serviceId: string; tier: ServiceTierSelection }>
    ) => {
      // Defensive: a blob rehydrated from storage can predate this key. The
      // store's migration backfills it, but a throw here silently kills the
      // dispatch and leaves the tier pickers looking inert, so never assume.
      if (!state.persisted.serviceTiers) {
        state.persisted.serviceTiers = {}
      }
      state.persisted.serviceTiers[action.payload.serviceId] = {
        ...state.persisted.serviceTiers[action.payload.serviceId],
        ...action.payload.tier
      }
    },
    setSelectedOption: (state, action: PayloadAction<BookingState['persisted']['selectedOption']>) => {
      state.persisted.selectedOption = action.payload
    },
    clearSelectedOption: (state) => {
      state.persisted.selectedOption = null
    },
    setContactInfo: (state, action: PayloadAction<BookingState['persisted']['contactInfo']>) => {
      state.persisted.contactInfo = action.payload
    },
    clearBookingState: (state) => {
      state.current.activeStep = 'vehicle';
      state.current.stepStates = { ...initialState.current.stepStates };
      state.current.visitedSteps = [];
      state.current.context = { ...initialState.current.context, activeStep: 'vehicle', validationErrors: [], isAuthenticated: false, canProceed: false };
      state.current.isDrawerOpen = false;
      state.current.isSubmitting = false;
      state.current.showVehicleSelect = false;
      state.current.userVehicles = [];
      state.current.isLoadingVehicles = false;
      state.current.isLoadingServices = false;
      state.current.vehicleModalShown = false;
      state.current.justAuthenticated = false;
      state.persisted = { ...initialState.persisted };
    },
    markStepAsVisited: (state, action: PayloadAction<BookingStep>) => {
      if (!state.current.visitedSteps) {
        state.current.visitedSteps = []
      }
      if (!state.current.visitedSteps.includes(action.payload)) {
        state.current.visitedSteps.push(action.payload)
      }
    }
  }
})

export const {
  setActiveStep,
  setStepState,
  setBookingContext,
  setDrawerOpen,
  setSubmitting,
  setShowVehicleSelect,
  setUserVehicles,
  setLoadingVehicles,
  setLoadingServices,
  setJustAuthenticated,
  setSelectedVehicleId,
  setBookingOwner,
  setShopLocation,
  setShopTimezone,
  setVehicleDetails,
  setSelectedServices,
  addSelectedService,
  removeSelectedService,
  setServiceTier,
  setSelectedOption,
  clearSelectedOption,
  setContactInfo,
  clearBookingState,
  markStepAsVisited
} = bookingSlice.actions

export default bookingSlice.reducer
